import React, { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, Eye, ToggleLeft, ToggleRight, Trash2, Plus, RefreshCw, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All 20 template IDs
const ALL_TEMPLATE_IDS = [
  "architect", "codebase", "sentinel", "dataforge", "pixelcraft",
  "ledger", "capital", "compass", "helm", "orbit",
  "cornerstone", "atelier", "drafting", "keystone", "momentum",
  "broadcast", "boardroom", "catalyst", "meridian", "narrative"
];

// Template categories for better organization
const TEMPLATE_CATEGORIES = {
  "Technology": ["architect", "codebase", "sentinel", "dataforge", "pixelcraft"],
  "BFSI": ["ledger", "capital"],
  "Maritime": ["compass", "helm"],
  "Telecom": ["orbit"],
  "Operations": ["cornerstone"],
  "Design": ["atelier", "drafting"],
  "Corporate": ["keystone", "momentum", "broadcast", "boardroom", "catalyst"],
  "Universal": ["meridian", "narrative"]
};

export default function DossierTemplatesAdminPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMethod, setImportMethod] = useState('file'); // 'file' or 'paste'
  const [pastedHtml, setPastedHtml] = useState('');
  const [pastedTemplateId, setPastedTemplateId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  const token = localStorage.getItem('ats_token');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/dossier-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      } else {
        toast.error('Failed to load templates');
      }
    } catch (e) {
      console.error('Error loading templates:', e);
      toast.error('Error loading templates');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.html')) {
        toast.error('Please select an HTML file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const formData = new FormData();
      
      if (importMethod === 'file' && selectedFile) {
        formData.append('file', selectedFile);
      } else if (importMethod === 'paste' && pastedHtml) {
        formData.append('html_content', pastedHtml);
        if (pastedTemplateId) {
          formData.append('template_id', pastedTemplateId.toLowerCase().trim());
        }
      } else {
        toast.error('Please provide a file or paste HTML content');
        setImporting(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/admin/dossier-templates/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || 'Template imported successfully');
        setShowImportModal(false);
        setSelectedFile(null);
        setPastedHtml('');
        setPastedTemplateId('');
        loadTemplates();
        
        // Auto-show preview
        if (data.template_id) {
          handlePreview(data.template_id);
        }
      } else {
        toast.error(data.detail || 'Failed to import template');
      }
    } catch (e) {
      console.error('Error importing template:', e);
      toast.error('Error importing template');
    }
    setImporting(false);
  };

  const handlePreview = async (templateId) => {
    setPreviewTemplate(templateId);
    try {
      const res = await fetch(`${API_URL}/api/admin/dossier-templates/${templateId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const html = await res.text();
        setPreviewHtml(html);
      } else {
        toast.error('Failed to load preview');
        setPreviewTemplate(null);
      }
    } catch (e) {
      console.error('Error loading preview:', e);
      toast.error('Error loading preview');
      setPreviewTemplate(null);
    }
  };

  const handleToggle = async (templateId, currentEnabled) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dossier-templates/${templateId}/toggle?enabled=${!currentEnabled}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Template ${!currentEnabled ? 'enabled' : 'disabled'}`);
        loadTemplates();
      } else {
        toast.error('Failed to toggle template');
      }
    } catch (e) {
      console.error('Error toggling template:', e);
      toast.error('Error toggling template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm(`Are you sure you want to delete template "${templateId}"?`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/dossier-templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Template deleted');
        loadTemplates();
        if (previewTemplate === templateId) {
          setPreviewTemplate(null);
          setPreviewHtml('');
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete template');
      }
    } catch (e) {
      console.error('Error deleting template:', e);
      toast.error('Error deleting template');
    }
  };

  const loadedTemplateIds = templates.map(t => t.template_id);
  const missingTemplates = ALL_TEMPLATE_IDS.filter(id => !loadedTemplateIds.includes(id));

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Dossier Template Manager</h1>
            <p className="text-slate-400 mt-1">Import and manage dossier templates for AI routing</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadTemplates}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition"
              data-testid="refresh-templates-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition"
              data-testid="import-template-btn"
            >
              <Plus className="w-4 h-4" />
              Import Template
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-3xl font-bold text-emerald-400">{templates.length}</div>
            <div className="text-slate-400 text-sm">Templates Loaded</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-3xl font-bold text-blue-400">{templates.filter(t => t.enabled).length}</div>
            <div className="text-slate-400 text-sm">Enabled</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-3xl font-bold text-amber-400">{missingTemplates.length}</div>
            <div className="text-slate-400 text-sm">Missing</div>
          </div>
        </div>

        {/* Missing Templates Alert */}
        {missingTemplates.length > 0 && (
          <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <div className="font-medium text-amber-300">Missing Templates</div>
                <div className="text-amber-200/80 text-sm mt-1">
                  The following templates need to be imported for full AI routing: {missingTemplates.join(', ')}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Templates List */}
          <div className="bg-slate-800 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <h2 className="font-semibold">Loaded Templates</h2>
            </div>
            
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading...</div>
            ) : templates.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                No templates loaded. Import your first template to get started.
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {templates.map((template) => (
                  <div 
                    key={template.template_id}
                    className={`p-4 flex items-center justify-between hover:bg-slate-700/50 transition ${
                      previewTemplate === template.template_id ? 'bg-slate-700/50' : ''
                    }`}
                    data-testid={`template-row-${template.template_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className={`w-5 h-5 ${template.enabled ? 'text-emerald-400' : 'text-slate-500'}`} />
                      <div>
                        <div className="font-medium">{template.template_id}</div>
                        <div className="text-xs text-slate-400">v{template.version || 1}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePreview(template.template_id)}
                        className="p-2 text-slate-400 hover:text-blue-400 transition"
                        title="Preview"
                        data-testid={`preview-${template.template_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleToggle(template.template_id, template.enabled)}
                        className={`p-2 transition ${template.enabled ? 'text-emerald-400 hover:text-emerald-300' : 'text-slate-500 hover:text-slate-400'}`}
                        title={template.enabled ? 'Disable' : 'Enable'}
                        data-testid={`toggle-${template.template_id}`}
                      >
                        {template.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      
                      <button
                        onClick={() => handleDelete(template.template_id)}
                        className="p-2 text-slate-400 hover:text-red-400 transition"
                        title="Delete"
                        data-testid={`delete-${template.template_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview Panel */}
          <div className="bg-slate-800 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="font-semibold">
                {previewTemplate ? `Preview: ${previewTemplate}` : 'Preview'}
              </h2>
              {previewTemplate && (
                <button
                  onClick={() => { setPreviewTemplate(null); setPreviewHtml(''); }}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {previewTemplate && previewHtml ? (
              <div className="h-[600px] overflow-auto bg-white">
                <iframe
                  srcDoc={previewHtml}
                  title="Template Preview"
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            ) : (
              <div className="h-[600px] flex items-center justify-center text-slate-400">
                Select a template to preview
              </div>
            )}
          </div>
        </div>

        {/* Template Categories Reference */}
        <div className="mt-8 bg-slate-800 rounded-lg p-6">
          <h2 className="font-semibold mb-4">Template Routing Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(TEMPLATE_CATEGORIES).map(([category, ids]) => (
              <div key={category} className="bg-slate-700/50 rounded-lg p-3">
                <div className="font-medium text-sm text-slate-300 mb-2">{category}</div>
                <div className="flex flex-wrap gap-1">
                  {ids.map(id => (
                    <span 
                      key={id}
                      className={`text-xs px-2 py-0.5 rounded ${
                        loadedTemplateIds.includes(id) 
                          ? 'bg-emerald-900/50 text-emerald-300' 
                          : 'bg-slate-600 text-slate-400'
                      }`}
                    >
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Import Template</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Method Toggle */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setImportMethod('file')}
                  className={`flex-1 py-2 px-4 rounded-lg transition ${
                    importMethod === 'file' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Upload File
                </button>
                <button
                  onClick={() => setImportMethod('paste')}
                  className={`flex-1 py-2 px-4 rounded-lg transition ${
                    importMethod === 'paste' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Paste HTML
                </button>
              </div>

              {importMethod === 'file' ? (
                <div className="space-y-4">
                  <div 
                    className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-slate-500 transition cursor-pointer"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <input
                      id="file-upload"
                      type="file"
                      accept=".html"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                    <div className="text-slate-300">
                      {selectedFile ? selectedFile.name : 'Click to select a .html file'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Template ID will be extracted from filename (e.g., dossier-architect.html)
                    </div>
                  </div>
                  
                  {selectedFile && (
                    <div className="flex items-center gap-2 p-3 bg-emerald-900/30 rounded-lg">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-300">{selectedFile.name}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Template ID *
                    </label>
                    <select
                      value={pastedTemplateId}
                      onChange={(e) => setPastedTemplateId(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a template ID</option>
                      {ALL_TEMPLATE_IDS.map(id => (
                        <option key={id} value={id}>
                          {id} {loadedTemplateIds.includes(id) ? '(update)' : '(new)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      HTML Content *
                    </label>
                    <textarea
                      value={pastedHtml}
                      onChange={(e) => setPastedHtml(e.target.value)}
                      placeholder="Paste the full HTML content of the template file..."
                      rows={12}
                      className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || (importMethod === 'file' && !selectedFile) || (importMethod === 'paste' && (!pastedHtml || !pastedTemplateId))}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg transition flex items-center gap-2"
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Import Template
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
